import { useState } from "react";
import { compareSectorNames } from "@/lib/sectorSort";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { toast } from "sonner";

interface MasterSector { id: string; name: string; sector_group: string; display_order: number; }
interface Commentary { id: string; portfolio: number; sector_id: string; comment: string | null; previous_comment: string | null; last_updated: string; }

const P = 1;

export default function MmSectorCommentary() {
  const queryClient = useQueryClient();
  const [edited, setEdited] = useState<Record<string, string>>({});

  const { data: sectors } = useQuery({
    queryKey: ["ba-master-sectors"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_master_sectors").select("*").order("display_order");
      if (error) throw error;
      return data as MasterSector[];
    },
  });
  const { data: commentary } = useQuery({
    queryKey: ["ba-mm-commentary"],
    queryFn: async () => {
      const { data, error } = await supabase.from("ba_mm_sector_commentary").select("*").eq("portfolio", P);
      if (error) throw error;
      return data as Commentary[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async ({ sectorId, comment }: { sectorId: string; comment: string }) => {
      const existing = commentary?.find((c) => c.sector_id === sectorId);
      const { error } = await supabase.from("ba_mm_sector_commentary").upsert({
        portfolio: P, sector_id: sectorId, comment, previous_comment: existing?.comment || null,
        last_updated: new Date().toISOString().split("T")[0],
      }, { onConflict: "portfolio,sector_id" });
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ba-mm-commentary"] });
      toast.success("Commentary saved");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const sortedSectors = [...(sectors ?? [])].sort((a, b) => compareSectorNames(a.name, b.name));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">MM Sector Commentary</h1>
        <p className="text-sm text-muted-foreground">Investment rationale for the Money Markets portfolio</p>
      </div>
      <Card>
        <CardContent className="space-y-3 pt-6">
          {sortedSectors.map((sector) => {
            const c = commentary?.find((x) => x.sector_id === sector.id);
            const currentText = edited[sector.id] ?? c?.comment ?? "";
            return (
              <div key={sector.id} className="border rounded-lg p-3 space-y-2">
                <span className="font-medium text-sm">{sector.name}</span>
                <Textarea value={currentText} onChange={(e) => setEdited({ ...edited, [sector.id]: e.target.value })} placeholder="Enter investment rationale..." className="text-xs min-h-[60px]" />
                <div className="flex items-center justify-end">
                  <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => saveMutation.mutate({ sectorId: sector.id, comment: currentText })}>
                    <Save className="h-3 w-3 mr-1" /> Save
                  </Button>
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
